import { Component, inject, OnInit, ChangeDetectorRef, effect, untracked, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-history',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './history.component.html'
})
export class HistoryComponent implements OnInit {
  appState = inject(AppStateService);
  authService = inject(AuthService);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);

  @Input() isEmbedded = false;

  activeTab = 'all';
  tabs = [
    { id: 'all', label: 'All Transactions' },
    { id: 'swaps', label: 'Swaps' },
    { id: 'liquidity', label: 'Liquidity' },
    { id: 'staking', label: 'Staking' },
    { id: 'farming', label: 'Farming' }
  ];

  transactions: any[] = [];
  isLoading = false;

  currentPage = 1;
  itemsPerPage = 10;
  totalRecords = 0;

  constructor() {
    effect(() => {
      if (this.authService.isAuthenticated()) {
        untracked(() => {
          this.fetchHistory();
        });
      } else {
        this.transactions = [];
        this.totalRecords = 0;
      }
    });
  }

  ngOnInit() {
    if (this.authService.isAuthenticated()) {
      this.fetchHistory();
    }
  }

  setTab(tabId: string) {
    this.activeTab = tabId;
    this.currentPage = 1;
    if (this.authService.isAuthenticated()) {
      this.fetchHistory();
    }
  }

  async fetchHistory() {
    try {
      this.isLoading = true;
      this.transactions = [];
      this.cdr.detectChanges();

      const cId = this.appState.selectedNetworkId();
      const url = `${environment.apiUrl}/user/history?tab=${this.activeTab}&page=${this.currentPage}&limit=${this.itemsPerPage}&chainId=${cId}`;
      const res: any = await firstValueFrom(
        this.http.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`
          }
        })
      );

      if (res && res.status && res.data) {
        this.transactions = res.data.data;
        this.totalRecords = res.data.count;
      }
    } catch (e) {
      console.error('Failed to load history', e);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  get totalPages() {
    return Math.ceil(this.totalRecords / this.itemsPerPage) || 1;
  }

  get paginationPages() {
    const pages = [];
    const total = this.totalPages;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (this.currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (this.currentPage >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', total);
      }
    }
    return pages;
  }

  goToPage(page: number | string) {
    if (typeof page === 'number') {
      this.currentPage = page;
      this.fetchHistory();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchHistory();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchHistory();
    }
  }

  getExplorerUrl(txHash: string): string {
    const netId = this.appState.selectedNetworkId();
    if (netId === 'eth') return `https://etherscan.io/tx/${txHash}`;
    if (netId === 'bnb') return `https://bscscan.com/tx/${txHash}`;
    if (netId === 'matic') return `https://polygonscan.com/tx/${txHash}`;
    if (netId === 'sepolia') return `https://sepolia.etherscan.io/tx/${txHash}`;
    return `https://etherscan.io/tx/${txHash}`; // default
  }

  formatWeiAmount(val: any, decimals: number = 18): string {
    if (!val) return '0';
    try {
      const valStr = val.toString();
      if (valStr === '0') return '0';
      if (valStr.includes('.')) {
        const num = Number(valStr);
        return parseFloat(num.toFixed(8)).toString(); 
      }
      let formatted = '0';
      try {
        const value = BigInt(valStr);
        const divisor = BigInt(10 ** decimals);
        const integerPart = value / divisor;
        const fractionalPart = value % divisor;
        formatted = integerPart.toString() + '.' + fractionalPart.toString().padStart(decimals, '0');
      } catch (e) {
        console.error("Format error", e);
      }
      return parseFloat(Number(formatted).toFixed(8)).toString();
    } catch {
      return '0';
    }
  }

  formatLegacyDetails(details: string | undefined): string {
    if (!details) return '';
    let formatted = details;
    // Replace "Swapped X for Y" with "X -> Y"
    if (formatted.startsWith('Swapped ') && formatted.includes(' for ')) {
      formatted = formatted.replace('Swapped ', '').replace(' for ', ' → ');
    }
    // Replace "Added Liquidity" or "Added X and Y"
    if (formatted.startsWith('Added ')) {
      formatted = formatted.replace('Added ', '+ ');
      formatted = formatted.replace(' and ', ' + ');
    }
    // Replace "Removed"
    if (formatted.startsWith('Removed ')) {
      formatted = formatted.replace('Removed ', '- ');
      formatted = formatted.replace(' and ', ' - ');
    }
    // Replace "Staked"
    if (formatted.startsWith('Staked ')) {
      formatted = formatted.replace('Staked ', '- ');
      formatted = formatted.replace(' in Farm', '');
    }
    // Replace "Unstaked"
    if (formatted.startsWith('Unstaked ')) {
      formatted = formatted.replace('Unstaked ', '+ ');
      formatted = formatted.replace(' from Farm', '');
    }
    // Replace "Claimed"
    if (formatted.startsWith('Claimed ')) {
      formatted = formatted.replace('Claimed ', '✨ ');
      formatted = formatted.replace('Rewards for ', '');
      formatted = formatted.replace('Farm Rewards for ', '');
    }
    return formatted;
  }

  formatTxType(type: string | undefined): string {
    if (!type) return 'Unknown';
    switch (type.toUpperCase()) {
      case 'SWAP': return 'Swapped';
      case 'ADD_LIQUIDITY': return 'Add liquidity';
      case 'REMOVE_LIQUIDITY': return 'Remove liquidity';
      case 'STAKE_NORMAL': return 'Staked';
      case 'UNSTAKE_NORMAL': return 'Unstaked';
      case 'FARM_STAKE': return 'Staked NFT';
      case 'FARM_UNSTAKE': return 'Unstaked NFT';
      case 'CLAIM_STAKE_REWARD':
      case 'FARM_CLAIM': return 'Claimed rewards';
      case 'CLAIM_FEE': return 'Collected fees';
      default: return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  }
}
